/**
 * Was man an einer Katalog-Aktion bearbeiten kann — und wie sie sich liest.
 *
 * Die Aktionen aus dem Nachtrag vom 07.08.2026 (Angebot, Zahlung, Bestellung,
 * Weiterleitung, Prüfergebnis, Projekt-Status) haben keine eigenen
 * Plan-Felder wie `plan.contact` oder `plan.slot`. Ihre Daten stehen in
 * `action.params`. Ohne diese Tabelle stand in der Karte nur der nackte
 * Aktionsname — der Nutzer sah nicht, worum es geht, und konnte nichts ändern.
 *
 * 🔑 Die Feldnamen müssen zu den Handlern in `services/action_handlers.py`
 * passen. Der Executor liest genau diese Schlüssel; ein Tippfehler hier
 * verschwindet still, weil ein unbekannter Parameter einfach ignoriert wird.
 */

export type Feld = {
  key: string
  label: string
  /** `text` einzeilig, `mehrzeilig` für Fließtext, `zahl` für Tage,
   *  `step` = Auswahl aus den HERO-Pipeline-Schritten (zeigt Namen, speichert
   *  die ID). Eine ID von Hand einzutippen ist keine Bedienung.
   *  `haken` = Ja/Nein-Häkchen, speichert `true` oder nichts (#160). */
  art?: "text" | "mehrzeilig" | "zahl" | "step" | "haken"
  hinweis?: string
  /** Ohne diesen Wert lehnt der Handler die Aktion ab. */
  pflicht?: boolean
}

export const AKTION_FELDER: Record<string, Feld[]> = {
  // ── Die Aktionen aus Schritt F. Sie hatten hier bis 07.08.2026 keinen
  //    Eintrag, weshalb „Bearbeiten" an ihnen ins Leere lief — der Dialog
  //    hatte schlicht keine Felder zu zeigen.
  rueckruf_notieren: [
    { key: "name", label: "Wen zurückrufen" },
    { key: "phone", label: "Nummer", hinweis: "So, wie sie in der Mail steht" },
    { key: "reason", label: "Worum es geht", art: "mehrzeilig" },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl", hinweis: "Ohne Angabe heute" },
  ],
  intern_informieren: [
    { key: "info", label: "Was weitergegeben wird", art: "mehrzeilig", pflicht: true },
    { key: "target_user_name", label: "An wen", hinweis: "Der Empfänger wird unten separat gewählt" },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl" },
  ],
  wiedervorlage: [
    { key: "subject", label: "Worum es geht", pflicht: true },
    { key: "note", label: "Notiz", art: "mehrzeilig" },
    { key: "due_date", label: "Fällig am", hinweis: "JJJJ-MM-TT — schlägt in_tagen" },
    { key: "in_tagen", label: "…oder in Tagen", art: "zahl", hinweis: "Ohne Angabe 3 Tage" },
  ],
  entscheidung_vorlegen: [
    { key: "question", label: "Frage", pflicht: true },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl", hinweis: "Ohne Angabe 2 Tage" },
  ],
  // 🔴 `dokument_ablegen` steht hier bewusst NICHT mehr.
  //    Es hatte ein nacktes Zahlenfeld „Projekt-ID", und der Planer hat am
  //    21.08.2026 eine „1" hineingeschrieben — eine geratene Zahl, die das
  //    verknüpfte Projekt geschlagen hätte. Abgelegt wird jetzt immer am
  //    Vorgang der Mail; soll es ein anderer sein, wird die Mail mit ihm
  //    verknüpft. Der Handler weist eine unbekannte ID zusätzlich ab.
  //    Und `[]` wäre falsch gewesen: ein leeres Array ist truthy, also hätte
  //    `zeigtDetailsBlock` weiter einen — nun leeren — Dialog geöffnet. Genau
  //    davor warnt `AktionBearbeiten.test.tsx`.
  kontakt_korrigieren: [
    { key: "contact_id", label: "Kontakt-ID", art: "zahl" },
    { key: "field", label: "Feld", pflicht: true,
      hinweis: "Nur email, phone_mobile, phone_home, first_name, last_name, company_name" },
    { key: "value", label: "Neuer Wert", pflicht: true },
  ],
  angebot_erstellen: [
    { key: "leistung", label: "Leistung", pflicht: true,
      hinweis: "Wofür das Angebot gemacht wird, z. B. „Duschenerneuerung“" },
    { key: "kunde", label: "Für wen" },
    { key: "umfang", label: "Umfang / Menge" },
    { key: "hinweis", label: "Hinweis", art: "mehrzeilig" },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl", hinweis: "Ohne Angabe 3 Tage" },
  ],
  zahlung_pruefen: [
    { key: "richtung", label: "Richtung", hinweis: "„eingang“ = Zahlungseingang überwachen, „ausgang“ = Rechnung prüfen und zahlen" },
    { key: "belegnummer", label: "Beleg / Rechnungsnummer" },
    { key: "betrag", label: "Betrag" },
    { key: "hinweis", label: "Hinweis", art: "mehrzeilig" },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl", hinweis: "Ohne Angabe 7 Tage" },
  ],
  bestellung_pruefen: [
    { key: "material", label: "Material / Leistung", pflicht: true },
    { key: "lieferant", label: "Lieferant" },
    { key: "belegnummer", label: "Bestell- / Belegnummer" },
    { key: "hinweis", label: "Hinweis", art: "mehrzeilig" },
    { key: "in_tagen", label: "Fällig in Tagen", art: "zahl", hinweis: "Ohne Angabe 2 Tage" },
  ],
  pruefergebnis_vermerken: [
    { key: "frage", label: "Geprüft wurde" },
    { key: "ergebnis", label: "Ergebnis", art: "mehrzeilig", pflicht: true,
      hinweis: "Landet im Logbuch des Vorgangs — keine Aufgabe" },
  ],
  mail_weiterleiten: [
    { key: "to", label: "An", pflicht: true,
      hinweis: "Nur Adressen aus den Weiterleitungs-Regeln des Betriebs" },
    { key: "note", label: "Notiz voranstellen", art: "mehrzeilig" },
  ],
  // ── Nachtrag 14.08.2026 ────────────────────────────────────────────────
  //    Beide kamen am 11.08. in den Katalog, der Eintrag hier wurde vergessen
  //    — zum zweiten Mal nach dem 07.08. Folge, gemeldet am 14.08.: die Zeile
  //    „Abrechnung am Vorgang vermerken" stand da, ohne dass man sah, WAS
  //    vermerkt wird. Wächter jetzt in
  //    `tests/test_aufgaben_aktionen_vollstaendig.py`.
  //    `abrechnung_hinterlegen` steht bewusst NICHT hier: die Aktion legt eine
  //    HERO-Aufgabe an und wird deshalb über den festen Aufgaben-Dialog
  //    bearbeitet, nicht über eine Feldliste. Ihre Zeile entsteht in
  //    `paramZeile`, sichtbar wird sie über `zeigtDetailsBlock`.
  projekt_adresse_setzen: [
    { key: "strasse", label: "Straße + Hausnummer", pflicht: true },
    { key: "plz", label: "PLZ" },
    { key: "ort", label: "Ort" },
    { key: "hinweis", label: "Hinweis", art: "mehrzeilig" },
    { key: "project_id", label: "Projekt-ID", art: "zahl",
      hinweis: "Leer = das an der Mail verknüpfte Projekt" },
  ],
  projekt_status_setzen: [
    // 🔴 Stand hier bis 07.08.2026 als Zahlenfeld „Pipeline-Schritt (ID)".
    // Mit „388504" kann niemand etwas anfangen — überall sonst im Portal
    // wählt man den Status über seine Bezeichnung. `step_name` wird beim
    // Auswählen automatisch mitgeschrieben, deshalb kein eigenes Feld.
    { key: "step_id", label: "Neuer Status", art: "step", pflicht: true },
  ],
  // Nachtrag 01.10.2026 — die Karte rendert dafür `TerminlinkAktion`
  // (Vorschläge + Kanal-Haken); die Felder hier sind die Bearbeiten-Felder.
  // Nachtrag 04.10.2026 (#155) — Absender:in als Ansprechpartner:in. Den
  // Kunden setzt der Handler aus dem Vorgang der Mail, deshalb kein Feld dafür.
  ansprechpartner_anlegen: [
    { key: "salutation", label: "Anrede", hinweis: "Frau / Herr / leer" },
    { key: "first_name", label: "Vorname" },
    { key: "last_name", label: "Nachname", pflicht: true },
    { key: "position", label: "Position/Funktion", hinweis: "z. B. Hausverwaltung, Mieterin" },
    { key: "email", label: "E-Mail-Adresse", hinweis: "Leer = Absenderadresse (nicht bei nur genannten Personen)" },
    { key: "phone", label: "Telefon" },
    // #160: Rechnungsempfänger:in als Ansprechpartner:in — Häkchen, Rolle,
    // Notizfeld + Logbuch. Wirkt erst beim Ausführen, Bearbeiten ändert nur den Vorschlag.
    { key: "rechnungsempfaenger", label: "Rechnungsempfänger", art: "haken",
      hinweis: "Rechnungen gehen an die Anschrift dieser Person" },
    { key: "notiz", label: "Notiz zur Rolle", art: "mehrzeilig",
      hinweis: "Kommt ins Notizfeld des Kontakts und ins Logbuch des Vorgangs" },
  ],
  terminlink_senden: [
    { key: "name", label: "Name (Person vor Ort)" },
    { key: "telefon", label: "Handynummer" },
    { key: "email", label: "E-Mail-Adresse" },
    { key: "info_an", label: "Info nach Bestätigung an" },
  ],
}

/** Die Zeile unter dem Aktionsnamen — spiegelt `preview()` im Backend-Katalog. */
export function paramZeile(type: string, p: Record<string, unknown>): string {
  const s = (k: string) => {
    const v = p[k]
    return v === null || v === undefined || v === "" ? "" : String(v)
  }
  const frist = s("due_date") || (s("in_tagen") ? `in ${s("in_tagen")} Tagen` : "")

  switch (type) {
    case "angebot_erstellen":
      return [
        s("leistung") || "(ohne Leistung)",
        s("kunde") && `für ${s("kunde")}`,
        s("umfang"),
        frist && `fällig ${frist}`,
      ].filter(Boolean).join(" · ")
    case "zahlung_pruefen":
      return [
        s("richtung") === "ausgang" ? "Rechnung prüfen und zahlen" : "Zahlungseingang überwachen",
        s("belegnummer"),
        s("betrag"),
        frist && `fällig ${frist}`,
      ].filter(Boolean).join(" · ")
    case "bestellung_pruefen":
      return [
        s("material") || "(ohne Angabe)",
        s("lieferant"),
        s("belegnummer"),
        frist && `fällig ${frist}`,
      ].filter(Boolean).join(" · ")
    case "pruefergebnis_vermerken":
      return [s("frage"), s("ergebnis")].filter(Boolean).join(" — ").slice(0, 160)
        || "(ohne Ergebnis)"
    case "mail_weiterleiten":
      return [s("to") || "(keine Adresse)", s("note")].filter(Boolean).join(" · ").slice(0, 160)
    case "projekt_status_setzen": {
      const ziel = s("step_name") || (s("step_id") ? `Schritt ${s("step_id")}` : "(kein Schritt)")
      return s("vorher_step_name") ? `${s("vorher_step_name")} → ${ziel}` : ziel
    }
    // #97 (26.10.2026): welcher Termin gelöscht wird — Datum, Uhrzeit, Titel,
    // Monteur. Die Termin-ID setzt das System und steht bewusst nicht hier.
    case "termin_stornieren":
      return [
        s("start") ? `${s("start").slice(8, 10)}.${s("start").slice(5, 7)}.${s("start").slice(0, 4)} ${s("start").slice(11, 16)}`
          + (s("ende") ? `–${s("ende")}` : "") + " Uhr" : "(kein Termin)",
        s("titel"),
        s("monteur") && `Monteur ${s("monteur")}`,
        "wird in HERO gelöscht",
      ].filter(Boolean).join(" · ")
    // HERO hat kein Feld für abweichende Rechnungsempfänger — der Vermerk
    // landet als Logbuch-Eintrag am Vorgang. Das gehört in die Zeile, sonst
    // bleibt offen, wohin die Angabe eigentlich geht.
    // Kurz halten: die Zeile ist schmal und wird nach zwei Zeilen abgeschnitten.
    // Adresse und Hinweis stehen im Bearbeiten-Dialog — was hier stehen MUSS,
    // ist wer was zahlt und wohin der Vermerk geht. Ohne das Ziel bleibt
    // offen, was die Aktion ueberhaupt anstellt (gemeldet 14.08.2026).
    case "abrechnung_hinterlegen":
      // Ist ein freier Logbuch-Text gesetzt, ist ER das, was im Vorgang landet
      // — dann muss die Zeile ihn zeigen und nicht die Zusammensetzung, die
      // gar nicht mehr geschrieben wird (#34).
      if (s("logbuch_text")) {
        return `Ins Logbuch des Vorgangs: ${s("logbuch_text")}`.slice(0, 200)
      }
      return `Ins Logbuch des Vorgangs: ${s("leistung") || "(ohne Leistung)"}`
        + ` · zahlt ${s("empfaenger") || "(offen)"}`
        + (s("adresse") ? `, Rechnung an ${s("adresse")}` : "")
    case "terminlink_senden": {
      const wege = (Array.isArray(p.kanaele) ? (p.kanaele as string[]) : [])
        .map((k) => (k === "sms" ? "SMS" : k === "email" ? "E-Mail" : k)).join(" + ")
      const n = Array.isArray(p.vorschlaege) ? p.vorschlaege.length : 0
      const personen = (Array.isArray(p.personen) ? p.personen : []) as { name?: string; rolle?: string }[]
      const zu = personen.filter((q) => q.rolle !== "info").map((q) => q.name).filter(Boolean).join(" + ")
      const info = personen.filter((q) => q.rolle === "info").length
        || (Array.isArray(p.info_an) ? p.info_an.length : s("info_an") ? 1 : 0)
      return [`An ${zu || s("name") || "Person vor Ort"}`, wege && `per ${wege}`, n && `${n} Vorschläge`,
        info && `Info an ${info}`].filter(Boolean).join(" · ")
    }
    case "ansprechpartner_anlegen": {
      const name = [s("first_name"), s("last_name")].filter(Boolean).join(" ")
      const re = p.rechnungsempfaenger === true || ["true", "ja", "1"].includes(s("rechnungsempfaenger").toLowerCase())
      return [name || s("email") || "Absender:in", s("position"),
        "als Ansprechpartner:in am Kunden des Vorgangs",
        re && "Rechnungsempfänger"].filter(Boolean).join(" · ")
    }
    case "projekt_adresse_setzen":
      return "Objektadresse: " + ([
        s("strasse"), [s("plz"), s("ort")].filter(Boolean).join(" "),
      ].filter(Boolean).join(", ") || "(ohne Angabe)")
    default:
      return ""
  }
}


export const LABELS: Record<string, string> = {
  kontakt_anlegen: "Kontakt anlegen",
  kontakt_korrigieren: "Kontaktdaten korrigieren",
  projekt_anlegen: "Projekt anlegen",
  termin_buchen: "Termin buchen",
  aufgabe_anlegen: "Aufgabe anlegen",
  rueckruf_notieren: "Rueckruf notieren",
  intern_informieren: "Kollegen informieren",
  wiedervorlage: "Wiedervorlage mit Frist",
  entscheidung_vorlegen: "Entscheidung vorlegen",
  dokument_ablegen: "Anhaenge im Vorgang ablegen",
  antwort_senden: "Antwort senden",
  // Nachtrag 07.08.2026 — ohne Eintrag hier zeigt die Karte den rohen
  // Schluessel in Kleinschrift ("angebot erstellen"), und der Nutzer sieht
  // einer Aktion nicht mehr an, was sie tut.
  pruefergebnis_vermerken: "Pruefergebnis vermerken",
  angebot_erstellen: "Angebot erstellen",
  zahlung_pruefen: "Zahlung pruefen",
  bestellung_pruefen: "Bestellung pruefen",
  mail_weiterleiten: "E-Mail weiterleiten",
  projekt_status_setzen: "Projekt-Status setzen",
  // Nachtrag 11.08.2026
  abrechnung_hinterlegen: "Abrechnung am Vorgang vermerken",
  projekt_adresse_setzen: "Objektadresse korrigieren",
  // Nachtrag 01.10.2026
  terminlink_senden: "Terminlink senden",
  // Nachtrag 04.10.2026 (#155)
  ansprechpartner_anlegen: "Ansprechpartner:in hinzufügen",
  // Nachtrag 26.10.2026 (#97)
  termin_stornieren: "Termin absagen",
}

/**
 * Aktionen, die in HERO als Aufgabe landen. Sie werden nicht ueber die
 * Feldliste bearbeitet, sondern ueber den festen Aufgaben-Dialog
 * (`TaskCreateModal`) — denselben, den das Portal ueberall zum Anlegen von
 * Aufgaben benutzt. Dort haengen Kontakt- und Projekt-Picker dran, und die
 * HERO-Regel „entweder Kontakt ODER Projekt" ist schon abgebildet.
 */
/**
 * 🔴 Diese Menge MUSS deckungsgleich sein mit den Handlern in
 * `services/action_handlers.py`, die `_hero_aufgabe()` aufrufen. Wächter:
 * `tests/test_aufgaben_aktionen_vollstaendig.py`.
 *
 * `abrechnung_hinterlegen` fehlte hier (Nachtrag 11.08., Eintrag hier
 * vergessen). Folge: die Zeile legte still eine zweite HERO-Aufgabe an, sah
 * in der Karte aber nicht danach aus — kein „Aufgabe für"-Empfänger, kein
 * Aufgaben-Dialog. Gemeldet 12.08.2026: „angelegt wurden zwei Aufgaben, im
 * Planner steht nur eine Zeile für eine Aufgabe."
 */
/**
 * Aktionen, deren Ergebnis ein Eintrag im HERO-Projekt-Logbuch ist.
 *
 * 🔑 Bewusst NUR die, bei denen das Logbuch der ZWECK ist. „Objektadresse
 * korrigieren" schreibt auch hinein, aendert aber wirklich die Adresse — das
 * Logbuch haelt dort nur den alten Wert fest. „Abrechnung am Vorgang
 * vermerken" schreibt Logbuch UND Wiedervorlage, und zwar mit Absicht: einen
 * Logbucheintrag allein liest beim Rechnungschreiben niemand. Beide als
 * „Logbuch" zu etikettieren wuerde verstecken, was sie tun.
 */
export const LOGBUCH_AKTIONEN = new Set([
  "pruefergebnis_vermerken", "dokument_ablegen",
])

/**
 * Aktionen, bei denen der Logbuch-Text frei geschrieben werden darf (#34).
 *
 * Gemeldet am 24.08.2026 von Marlene: „Den ‚Logbuch Eintrag‘ brauchen wir
 * immer als fixe Info in einem speziellen Eingabefeld oder so." Der Eintrag
 * entstand bis dahin ausschließlich aus leistung/empfaenger/adresse — was
 * dort steht, ließ sich nicht beeinflussen, obwohl es der Text ist, den beim
 * Rechnungschreiben jemand lesen soll.
 *
 * 🔑 Der Aufgaben-Dialog bleibt unangetastet: er ist derselbe, den „Neue
 * Aufgabe" überall öffnet (gemeldet 07.08.2026: „ich will das gleiche
 * popup"). Ein Feld, das nur eine Aktion braucht, gehört nicht hinein — es
 * steht deshalb direkt an der Karte.
 */
export const FREIER_LOGBUCH_TEXT = new Set([
  "abrechnung_hinterlegen",
])

export const AUFGABEN_AKTIONEN = new Set([
  "rueckruf_notieren", "intern_informieren", "wiedervorlage",
  "entscheidung_vorlegen", "angebot_erstellen", "zahlung_pruefen",
  "bestellung_pruefen", "abrechnung_hinterlegen",
])

/**
 * Bekommt diese Aktion unter ihrem Titel einen Detail-Block — also die Zeile
 * mit ihren Angaben und den Bearbeiten-Knopf?
 *
 * Zwei Wege dorthin, und genau das war die Falle: eine Feldliste in
 * `AKTION_FELDER` **oder** der feste Aufgaben-Dialog für Aktionen, die eine
 * HERO-Aufgabe anlegen. Die Bedingung stand an drei Stellen als
 * `AKTION_FELDER[type]` — wer beides nicht hatte, blieb ohne Detail-Block.
 * So verschwand am 14.08.2026 der Inhalt von „Abrechnung am Vorgang
 * vermerken": die Aktion wird über den Aufgaben-Dialog bearbeitet und
 * brauchte deshalb nie eine Feldliste, wurde ohne sie aber gar nicht erst
 * angezeigt.
 */
export function zeigtDetailsBlock(type: string): boolean {
  return Boolean(AKTION_FELDER[type]) || AUFGABEN_AKTIONEN.has(type)
}

export function label(type: string): string {
  return LABELS[type] || type.replace(/_/g, " ")
}

/**
 * Welcher Plan-Schritt zu einer Katalog-Aktion gehoert.
 *
 * Die Karte kannte bisher nur `step_enabled` (contact/project/slot/…), die
 * Liste darunter nur die Aktions-Typen. Ein Haken steuert jetzt beides;
 * Aktionen ohne eigenen Schritt (Rueckruf, Wiedervorlage, …) geben null.
 */
export function schrittFuerAktion(type: string): "contact" | "project" | "slot" | "monteur_task" | "reply" | null {
  return ({
    kontakt_anlegen: "contact",
    kontakt_korrigieren: "contact",
    projekt_anlegen: "project",
    termin_buchen: "slot",
    aufgabe_anlegen: "monteur_task",
    antwort_senden: "reply",
  } as const)[type] ?? null
}


/**
 * Titel-Vorschlag für den Aufgaben-Dialog.
 *
 * Er muss dem entsprechen, was der Handler ohne Zutun bauen würde — sonst
 * sieht der Nutzer im Dialog etwas anderes, als beim Ausführen entsteht.
 * Gegenstück zu den Titeln in `services/action_handlers.py`.
 */
export function vorschlagTitel(type: string, p: Record<string, unknown>): string {
  const s = (k: string) => String(p[k] ?? "").trim()
  switch (type) {
    case "rueckruf_notieren":
      return `Rueckruf an ${s("name") || "den Absender"}`
    case "intern_informieren":
      return s("target_user_name") ? `Info fuer ${s("target_user_name")}` : "Interne Info"
    case "wiedervorlage":
      return `Wiedervorlage: ${s("subject")}`.trim()
    case "entscheidung_vorlegen":
      return `Entscheidung: ${s("question")}`.trim()
    case "angebot_erstellen":
      return `Angebot erstellen: ${s("leistung")}` + (s("kunde") ? ` (${s("kunde")})` : "")
    case "zahlung_pruefen": {
      const was = s("richtung") === "ausgang" ? "Rechnung pruefen und zahlen" : "Zahlungseingang pruefen"
      return was + (s("belegnummer") ? `: ${s("belegnummer")}` : "")
    }
    case "bestellung_pruefen":
      return `Bestellung pruefen: ${s("material")}` + (s("lieferant") ? ` (${s("lieferant")})` : "")
    default:
      return label(type)
  }
}
